import React from "react";
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  Button,
  Box,
  Rating,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import LoadingView from "./LoadingView";
import ErrorView from "./ErrorView";
import { lightBlue } from "@mui/material/colors";
import { ASSET_CODINGPAL } from "app/utils/constants/paths";
import usePlans from "app/hooks/usePlans";
import { ROUTES } from "app/utils/constants/routeConstants";
import useLoginRedirect from "app/hooks/useLoginRedirect";

const PricingCards = ({ size = "medium" }) => {
  const navigate = useNavigate();
  const { authUser } = useJumboAuth();
  const { plans, isLoading, error } = usePlans();
  const isLoggedIn = !!authUser;

  const { handleProtectedAction, LoginDialog } = useLoginRedirect({
    isLoggedIn,
    dialogMessage: "Please login to reach out to us.",
  });

  const sizeConfig = {
    small: {
      titleFontSize: "1rem",
      secondaryFontSize: "0.75rem",
      imageWidth: 100,
      imageHeight: 70,
      buttonSize: "small",
      padding: 1,
    },
    medium: {
      titleFontSize: "1.25rem",
      secondaryFontSize: "0.875rem",
      imageWidth: 120,
      imageHeight: 84,
      buttonSize: "medium",
      padding: 1.5,
    },
    large: {
      titleFontSize: "1.5rem",
      secondaryFontSize: "1rem",
      imageWidth: 140,
      imageHeight: 98,
      buttonSize: "large",
      padding: 2,
    },
  };

  const config = sizeConfig[size] || sizeConfig.medium;

  if (isLoading) {
    return <LoadingView message="Loading plans..." />;
  }

  if (error) {
    return (
      <ErrorView
        error={error}
        onBack={() => navigate(-1)}
        backButtonLabel="Back"
      />
    );
  }

  const handleViewDetails = () => {
    const queryParams = new URLSearchParams({
      size: "25",
      page: "1",
      sort: "id,asc",
      ProblemVideos: "true",
    });
    navigate({
      pathname: ROUTES.ALGOS_AND_DS,
      search: queryParams.toString(),
    });
  };

  return (
    <>
      <List sx={{ width: "100%", bgcolor: "background.paper", py: 0 }}>
        {plans.map((plan) => {
          return (
            <ListItem
              key={plan.id || `plan-${plan.name}`}
              sx={{
                p: (theme) => theme.spacing(config.padding, 3),
                "&:hover": {
                  cursor: "pointer",
                  backgroundColor: "#F5F5F5",
                  borderRadius: 2,
                },
              }}
            >
              <ListItemAvatar
                sx={{
                  mr: 2,
                  width: config.imageWidth,
                  overflow: "hidden",
                  borderRadius: 2,
                }}
              >
                <img
                  width={config.imageWidth}
                  height={config.imageHeight}
                  alt={plan.name}
                  style={{ verticalAlign: "middle", objectFit: "contain" }}
                  src={
                    plan.thumbnail && plan.thumbnailContentType
                      ? `data:${plan.thumbnailContentType};base64,${plan.thumbnail}`
                      : plan.thumbnail || `${ASSET_CODINGPAL}/default-plan.png`
                  }
                />
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography
                    variant="h5"
                    sx={{ fontSize: config.titleFontSize, mb: 0.25 }}
                  >
                    {plan.name}
                  </Typography>
                }
                secondary={
                  <Box component="div">
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        color="text.secondary"
                        variant="body1"
                        sx={{ fontSize: config.secondaryFontSize, mb: 1 }}
                      >
                        {plan.price === 0
                          ? "Free"
                          : `₹${(plan.price / 100).toFixed(0)} / ${
                              plan.validity
                            } ${plan.validityUnit.toLowerCase()}`}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {plan.rating ? (
                          <>
                            <Rating
                              name="course-rating"
                              value={plan.rating}
                              precision={0.5}
                              readOnly
                              size="small"
                            />
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not Rated
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Typography
                      variant="body1"
                      sx={{ fontSize: config.secondaryFontSize, mb: 0.5 }}
                    >
                      {plan.numberOfLectures}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                      {/* <Button
                        size={config.buttonSize}
                        onClick={() => {
                          handleProtectedAction(ROUTES.REPORT, {
                            queryParams: { planId: plan.id || "unknown" },
                          });
                        }}
                        sx={{
                          textTransform: "none",
                          fontSize: config.secondaryFontSize,
                          "&:hover": {
                            color: lightBlue[600],
                            backgroundColor: lightBlue[50],
                          },
                        }}
                      >
                        Reach out to us
                      </Button> */}
                      <Button
                        size={config.buttonSize}
                        onClick={handleViewDetails}
                        sx={{
                          textTransform: "none",
                          fontSize: config.secondaryFontSize,
                        }}
                      >
                        Details
                      </Button>
                    </Box>
                  </Box>
                }
              />
            </ListItem>
          );
        })}
      </List>
      {LoginDialog}
    </>
  );
};

export default PricingCards;
